import { di } from 'react-magnetic-di';

import { useAgentsPaginatedByAgentId } from '@atlassian/conversation-assistant-agent/controllers/agents';
import { type StoreAssistant } from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { deriveResponseAuthorIdentity } from './deriveResponseAuthorIdentity';
import { getResponseHatRenderState } from './getResponseHatRenderState';
import { type ResponseAuthorIdentity } from './ResponseAuthorIdentity';

type AssistanceServiceParams = Parameters<
	typeof useAgentsPaginatedByAgentId
>[0]['assistanceServiceParams'];

const IDENTITY_USER_ARI_PREFIX = 'ari:cloud:identity::user/';

const getIdentityUserAri = (ari: string | null | undefined): string | undefined => {
	const trimmed = ari?.trim();
	return trimmed?.startsWith(IDENTITY_USER_ARI_PREFIX) ? trimmed : undefined;
};

export type UseResponseHatStateParams = {
	storeAssistant: StoreAssistant;
	previousAssistantAuthor?: ResponseAuthorIdentity;
	shouldEnableAgentResponseHat: boolean;
	isConversationActivelyGenerating: boolean;
	canRenderAfterThinking: boolean;
	hasAnswerContent: boolean;
	assistanceServiceParams: AssistanceServiceParams;
};

export type ResponseHatState = {
	shouldRender: boolean;
	isDefaultRovo: boolean;
	identityAccountId: string | undefined;
	isHydrating: boolean;
};

export const useResponseHatState = ({
	storeAssistant,
	previousAssistantAuthor,
	shouldEnableAgentResponseHat,
	isConversationActivelyGenerating,
	canRenderAfterThinking,
	hasAnswerContent,
	assistanceServiceParams,
}: UseResponseHatStateParams): ResponseHatState => {
	di(useAgentsPaginatedByAgentId);

	const {
		content: {
			message: {
				message: { author },
			},
			streamingComplete = true,
		},
	} = storeAssistant;
	const currentAuthor = deriveResponseAuthorIdentity(storeAssistant);
	const { agent: responseAuthorAgent } = useAgentsPaginatedByAgentId({
		agentId:
			shouldEnableAgentResponseHat && !currentAuthor.isDefault ? currentAuthor.id : undefined,
		assistanceServiceParams,
	});
	const renderState = getResponseHatRenderState({
		shouldEnableAgentResponseHat,
		canRenderAfterThinking,
		currentResponseAuthor: currentAuthor,
		previousAssistantAuthor,
		responseAuthorAgent,
		backendAuthorIdentityAccountId: author?.identity_account_id ?? getIdentityUserAri(author?.ari),
		isConversationActivelyGenerating,
		streamingComplete: Boolean(streamingComplete),
		hasAnswerContent,
	});

	return {
		shouldRender: renderState.shouldRenderAgentResponseHat,
		isDefaultRovo: renderState.isDefaultRovo,
		identityAccountId: renderState.agentIdentityAccountId,
		isHydrating: renderState.isIdentityHydrating,
	};
};
